import type { PaymentMethod } from '@/types/checkout';
import type { PaymentMethod as StripePaymentMethod } from '@stripe/stripe-js';
import { ProviderEnum } from '@/types/common';

interface AmazonPayParams {
  paymentMethodId: string;
  paymentMethodToken: string;
  paymentMethodDetails: {
    id: string;
    object: string;
    allow_redisplay: string;
    amazon_pay: Record<string, never>;
    billing_details: {
      address: {
        city: string | null;
        country: string | null;
        line1: string | null;
        line2: string | null;
        postal_code: string | null;
        state: string | null;
      };
      email: string | null;
      name: string | null;
      phone: string | null;
      tax_id: null;
    };
    created: number;
    customer: string | null;
    livemode: boolean;
    type: string;
  };
  shouldSaveCard: boolean;
  fraudDetection: {
    forterTokenCookie: string;
  };
}

export interface StripeAmazonPayRequest {
  paymentProvider: string;
  receipt_email: string;
  params: AmazonPayParams;
  paymentMethodDisplayName: string;
  paymentMethodId: string;
}

// Helper function to get Forter token from cookies
const getForterTokenFromCookie = (): string => {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'forterToken' || name === '_ftr') {
      return value;
    }
  }
  return '';
};

export const buildStripeAmazonPayRequest = (
  paymentMethod: StripePaymentMethod,
  providerSettings: PaymentMethod,
  email: string
): StripeAmazonPayRequest => {
  const params: AmazonPayParams = {
    paymentMethodId: paymentMethod.id,
    paymentMethodToken: paymentMethod.id,
    paymentMethodDetails: {
      id: paymentMethod.id,
      object: 'payment_method',
      allow_redisplay: 'unspecified',
      amazon_pay: {},
      billing_details: {
        address: {
          city: paymentMethod.billing_details?.address?.city || null,
          country: paymentMethod.billing_details?.address?.country || null,
          line1: paymentMethod.billing_details?.address?.line1 || null,
          line2: paymentMethod.billing_details?.address?.line2 || null,
          postal_code: paymentMethod.billing_details?.address?.postal_code || null,
          state: paymentMethod.billing_details?.address?.state || null,
        },
        email: paymentMethod.billing_details?.email || email || null,
        name: paymentMethod.billing_details?.name || null,
        phone: paymentMethod.billing_details?.phone || null,
        tax_id: null,
      },
      created: paymentMethod.created,
      customer: paymentMethod.customer as string | null,
      livemode: paymentMethod.livemode,
      type: 'amazon_pay',
    },
    shouldSaveCard: false,
    fraudDetection: {
      forterTokenCookie: getForterTokenFromCookie(),
    },
  };

  return {
    paymentProvider: 'stripe',
    receipt_email: email || paymentMethod.billing_details?.email || '',
    params,
    paymentMethodDisplayName: 'Amazon Pay',
    paymentMethodId: providerSettings.id,
  };
};

/**
 * Finalize request for the Amazon Pay Express Checkout Element (ECE) flow.
 *
 * Shape mirrors {@link buildStripeBlikPaymentRequest}: `params` is intentionally
 * empty. Amazon Pay ECE can't tokenize into a Stripe `PaymentMethod` client-side
 * (Stripe's ECE probe drops redirect wallets whenever the parent `<Elements>` is
 * in `paymentMethodCreation: 'manual'` mode, so `stripe.createPaymentMethod`
 * isn't available for this integration). Instead, the BE creates an unconfirmed
 * `PaymentIntent` with `payment_method_types: ['amazon_pay']` (keyed off the AC
 * settings PM id in the outer envelope), returns its `client_secret`, and the
 * client finalizes via `stripe.confirmPayment({ elements, clientSecret, ... })`
 * so Stripe mints the PM against the Amazon session it already holds.
 */
export const buildStripeAmazonPayExpressRequest = (providerSettings: PaymentMethod, email: string) => {
  return {
    paymentProvider: ProviderEnum.STRIPE,
    receipt_email: email,
    params: {},
    paymentMethodDisplayName: 'Amazon Pay',
    paymentMethodId: providerSettings.id,
  };
};
